/**
 * Signup API Utility
 *
 * Dedicated API functions for the multi-step signup flow.
 * Uses pre-auth API (no JWT tokens) since users aren't authenticated yet.
 *
 * Flow:
 * 1. initiateSignup - Collect name/email, get signupToken
 * 2. verifyEmail - Verify email with 6-digit code
 * 3. selectPlan - Choose tier, get Stripe URL for paid tiers
 * 4. setPassword - Create Cognito user with password (after payment for paid tiers)
 * 5. completeSignup - Finalize registration (for free tier)
 *
 * All endpoints use signupToken for authentication (stored in PendingSignups table).
 */

import { logger } from './logger';
import { preAuthPost, preAuthGet } from './preAuthApi';

// Types for signup API responses

export interface SignupInitiateResponse {
  success: boolean;
  signupToken: string | null;  // null when email already exists (account enumeration protection)
  cognitoUsername?: string;    // Cognito username when using Cognito verification flow
  message: string;
  error?: string;
}

export interface SignupVerifyEmailResponse {
  success: boolean;
  message: string;
  emailVerified?: boolean;
  status?: string; // Backend returns 'email_verified' on success
  error?: string;
}

export interface SignupSelectPlanResponse {
  success: boolean;
  message: string;
  tier: string;
  requiresPayment: boolean;
  stripeCheckoutUrl?: string;
  error?: string;
}

export interface SignupSetPasswordResponse {
  success: boolean;
  message: string;
  userId?: string;
  error?: string;
}

export interface SignupCompleteResponse {
  success: boolean;
  message: string;
  userId: string;
  error?: string;
}

export interface SignupStatusResponse {
  exists: boolean;
  status: string;
  emailVerified: boolean;
  tier?: string;
  paymentComplete?: boolean;
  passwordSet?: boolean;
  mfaConfigured?: boolean;
  error?: string;
}

export interface AbandonSignupResponse {
  success: boolean;
  message: string;
  error?: string;
}

export interface MfaStartResponse {
  success: boolean;
  message: string;
  options?: unknown;
  qrCode?: string;
  secret?: string;
  error?: string;
}

export interface MfaCompleteResponse {
  success: boolean;
  message: string;
  mfaEnabled: boolean;
  error?: string;
  authTokens?: {
    idToken: string;
    accessToken: string;
    refreshToken: string;
  };
}

/**
 * Step 1: Initiate signup - collect contact info & display name, send verification code
 *
 * @param data - User contact information
 * @returns SignupToken and confirmation message
 */
export async function initiateSignup(data: {
  email: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  initials?: string;  // 2-letter initials for ALI ID (auto-generated from name if not provided)
  organization?: string;
  privacyPolicyVersion: string;
  tosVersion: string;
}): Promise<SignupInitiateResponse> {
  try {
    const response = await preAuthPost('/api/signup/initiate', data);
    return response as SignupInitiateResponse;
  } catch (error) {
    logger.error('[Signup API] initiateSignup error', error);
    return {
      success: false,
      signupToken: null,
      message: 'Failed to initiate signup',
      error: (error as Error).message,
    };
  }
}

/**
 * Step 2: Verify email with 6-digit code
 *
 * @param signupToken - Token from initiateSignup
 * @param code - 6-digit verification code
 * @returns Verification status
 */
export async function verifyEmail(
  signupToken: string,
  code: string
): Promise<SignupVerifyEmailResponse> {
  try {
    const response = await preAuthPost('/api/signup/verify-email', {
      signupToken,
      verificationCode: code,
    });
    return response as SignupVerifyEmailResponse;
  } catch (error) {
    logger.error('[Signup API] verifyEmail error', error);
    return {
      success: false,
      message: 'Failed to verify email',
      emailVerified: false,
      error: (error as Error).message,
    };
  }
}

/**
 * Resend email verification code
 *
 * @param signupToken - Token from initiateSignup
 * @returns Success status
 */
export async function resendVerificationCode(
  signupToken: string
): Promise<{ success: boolean; message: string; error?: string }> {
  try {
    const response = await preAuthPost('/api/signup/resend-verification', {
      signupToken,
    });
    return response as { success: boolean; message: string; error?: string };
  } catch (error) {
    logger.error('[Signup API] resendVerificationCode error', error);
    return {
      success: false,
      message: 'Failed to resend verification code',
      error: (error as Error).message,
    };
  }
}

/**
 * Step 3: Select subscription tier/plan
 *
 * @param signupToken - Token from initiateSignup
 * @param tier - Selected tier: 'viewer' | 'creator' | 'commercial'
 * @param initials - Optional 2-letter initials for ALI ID (required for paid tiers upgrading at Step 3)
 * @returns Plan selection status, Stripe URL for paid tiers
 */
export async function selectPlan(
  signupToken: string,
  tier: 'viewer' | 'creator' | 'commercial',
  initials?: string,
  businessName?: string,
  businessType?: string,
  billingPeriod?: 'monthly' | 'yearly',
): Promise<SignupSelectPlanResponse> {
  try {
    const body: Record<string, string> = { signupToken, tier };
    if (initials) {
      body.initials = initials;
    }
    if (businessName) {
      body.businessName = businessName;
    }
    if (businessType) {
      body.businessType = businessType;
    }
    if (billingPeriod) {
      body.billingPeriod = billingPeriod;
    }
    const response = await preAuthPost('/api/signup/select-plan', body);
    return response as SignupSelectPlanResponse;
  } catch (error) {
    logger.error('[Signup API] selectPlan error', error);
    return {
      success: false,
      message: 'Failed to select plan',
      tier,
      requiresPayment: false,
      error: (error as Error).message,
    };
  }
}

/**
 * Step 5: Set password and create Cognito user
 *
 * @param signupToken - Token from initiateSignup
 * @param password - User's password (must meet Cognito requirements)
 * @returns User creation status
 */
export async function setPassword(
  signupToken: string,
  password: string
): Promise<SignupSetPasswordResponse> {
  try {
    const response = await preAuthPost('/api/signup/set-password', {
      signupToken,
      password,
    });
    return response as SignupSetPasswordResponse;
  } catch (error) {
    logger.error('[Signup API] setPassword error', error);
    return {
      success: false,
      message: 'Failed to set password',
      error: (error as Error).message,
    };
  }
}

/**
 * Complete signup (for free tier users who skip MFA)
 *
 * @param signupToken - Token from initiateSignup
 * @returns Completion status with userId
 */
export async function completeSignup(signupToken: string): Promise<SignupCompleteResponse> {
  try {
    const response = await preAuthPost('/api/signup/complete', {
      signupToken,
    });
    return response as SignupCompleteResponse;
  } catch (error) {
    logger.error('[Signup API] completeSignup error', error);
    return {
      success: false,
      message: 'Failed to complete signup',
      userId: '',
      error: (error as Error).message,
    };
  }
}

/**
 * Get current signup status
 *
 * @param signupToken - Token from initiateSignup
 * @returns Current signup state
 */
export async function getSignupStatus(signupToken: string): Promise<SignupStatusResponse> {
  try {
    const response = await preAuthGet('/api/signup/status', { signupToken });
    return response as SignupStatusResponse;
  } catch (error) {
    logger.error('[Signup API] getSignupStatus error', error);
    return {
      exists: false,
      status: 'unknown',
      emailVerified: false,
      error: (error as Error).message,
    };
  }
}

/**
 * Abandon an in-progress signup session
 *
 * Deletes both the Cognito user (if created) and the DynamoDB pending signup record.
 * Use this when a user wants to start fresh or when signup state becomes corrupted.
 *
 * @param signupToken - Token from initiateSignup
 * @returns Success status
 */
export async function abandonSignup(signupToken: string): Promise<AbandonSignupResponse> {
  try {
    const response = await preAuthPost('/api/signup/abandon', {
      signupToken,
    });
    return response as AbandonSignupResponse;
  } catch (error) {
    logger.error('[Signup API] abandonSignup error', error);
    return {
      success: false,
      message: 'Failed to abandon signup session',
      error: (error as Error).message,
    };
  }
}

// MFA Setup APIs (for signup flow - uses signupToken instead of JWT)

/**
 * Start TOTP MFA setup during signup
 *
 * @param signupToken - Token from initiateSignup
 * @returns QR code and secret for authenticator app
 */
export async function startTotpMfaSetup(signupToken: string): Promise<MfaStartResponse> {
  try {
    const response = await preAuthPost('/api/signup/mfa/totp/start', {
      signupToken,
    });
    return response as MfaStartResponse;
  } catch (error) {
    logger.error('[Signup API] startTotpMfaSetup error', error);
    return {
      success: false,
      message: 'Failed to start TOTP setup',
      error: (error as Error).message,
    };
  }
}

/**
 * Complete TOTP MFA setup during signup
 *
 * @param signupToken - Token from initiateSignup
 * @param code - 6-digit TOTP code from authenticator app
 * @returns MFA setup status
 */
export async function completeTotpMfaSetup(
  signupToken: string,
  code: string
): Promise<MfaCompleteResponse> {
  try {
    const response = await preAuthPost('/api/signup/mfa/totp/complete', {
      signupToken,
      totpCode: code,
    });
    return response as MfaCompleteResponse;
  } catch (error) {
    logger.error('[Signup API] completeTotpMfaSetup error', error);
    return {
      success: false,
      message: 'Failed to complete TOTP setup',
      mfaEnabled: false,
      error: (error as Error).message,
    };
  }
}

/**
 * Start SMS MFA setup during signup
 *
 * @param signupToken - Token from initiateSignup
 * @param phoneNumber - Phone number in E.164 format (e.g., +12025551234)
 * @returns Verification code sent status
 */
export async function startSmsMfaSetup(
  signupToken: string,
  phoneNumber: string
): Promise<MfaStartResponse> {
  try {
    const response = await preAuthPost('/api/signup/mfa/sms/start', {
      signupToken,
      phoneNumber,
    });
    return response as MfaStartResponse;
  } catch (error) {
    logger.error('[Signup API] startSmsMfaSetup error', error);
    return {
      success: false,
      message: 'Failed to start SMS setup',
      error: (error as Error).message,
    };
  }
}

/**
 * Verify SMS MFA setup during signup
 *
 * @param signupToken - Token from initiateSignup
 * @param code - 6-digit SMS verification code
 * @returns MFA setup status
 */
export async function verifySmsMfaSetup(
  signupToken: string,
  code: string
): Promise<MfaCompleteResponse> {
  try {
    const response = await preAuthPost('/api/signup/mfa/sms/verify', {
      signupToken,
      smsCode: code,
    });
    return response as MfaCompleteResponse;
  } catch (error) {
    logger.error('[Signup API] verifySmsMfaSetup error', error);
    return {
      success: false,
      message: 'Failed to verify SMS setup',
      mfaEnabled: false,
      error: (error as Error).message,
    };
  }
}
